import takeRight from "lodash/takeRight";
import isEqual from "lodash/isEqual";

import {getCurrentLayout, getCurrentRoomType} from "../../appLayout";
import {
  browserIsSafari,
  getCartItemObject,
  getFeatureIsLocked,
  getRelatedFeaturesFromLayout,
  getShareUrl
} from "../../../misc/helpers";
import {checkIfFeatureCanBeAddedToTheCart, getLookList} from "./lookFunctions";
import {shareImage} from "../../bvComponent/core/assets";
import {loaderDisable, loaderEnable} from "../../ui/loaderView";
import {saveQuote} from "../../../api/server-api";
import {setAmazonImageUrl} from "../../../actions/serverFunctions";
import {getShareConfig, SHARE_BUTTONS} from "./lookSideBar/lookSidebarModals/lookSideBarShareButtons";
import store from "../../../store";
import {LOOK_ROOM_TYPES, LOOK_WALLS} from "../core/constants";
import {IS_LOOK_JOURNEY, IS_PRODUCT_JOURNEY} from "../../../config";
import {allowCheckForCollection} from "../grohe/groheNavigation/groheFunctions";
import {CART_ITEM_ADDED_BY} from "../../shoppingCart/cartItemInterface";
import {isFeatureEnabled} from "../../../misc/displayLogic";
import Cabinet from "../../kvSceneComponent/core/parts/cabinet";
import {checkIfLookFeaturesReceived, getAdditionalFeaturesOnScroll} from "../../../api/look-api";
import {getCurrentGroups, getFeatureGroupFromWizardGroup} from "./lookMain/lookWizard/lookWizardFunctions";

/**
 * disable Feature common function
 * @param feature {featureInterface}
 * @param updateLookCartItem {function}
 */
export const disableFeature = async (feature, updateLookCartItem = null) => {
  if (!feature) {
    return
  }
  switch (feature.partId) {
    case "hood": {
      const cabinetParent = feature.getParent().getParent().layout.parts[Cabinet.id];
      if (cabinetParent && cabinetParent.topCabinet && !cabinetParent.topCabinet.allFeaturesReceived) {
        loaderEnable();
        await getAdditionalFeaturesOnScroll(cabinetParent.topCabinet)
        loaderDisable();
      }
      break;
    }
    case "center_island_cabinet": {
      const floorParent = feature.getParent().getParent().layout.parts["Floor"];
      if (floorParent && floorParent.floorBody && !floorParent.floorBody.allFeaturesReceived) {
        loaderEnable();
        await getAdditionalFeaturesOnScroll(floorParent.floorBody)
        loaderDisable();
      }
      break;
    }
    case "fridge": {
      const cabinetParent = feature.getParent().getParent().layout.parts["Cabinet"];
      if (cabinetParent && cabinetParent.topCabinet && !cabinetParent.topCabinet.allFeaturesReceived) {
        loaderEnable();
        await checkIfLookFeaturesReceived(cabinetParent.topCabinet)
        loaderDisable();
      }
      break;
    }
    default:
      break;
  }
  // console.log("removeFromCartArray", removeFromCartArray);
  const featureGroup = feature.getParent();
  if (!featureGroup || !featureGroup.canBeDisabled) {
    return;
  }
  if (featureGroup.layerPositions) {
    Object.keys(featureGroup.layerPositions).forEach(position => {
      featureGroup.layerPositions[position] = null;
    })
  }
  featureGroup.disabled = true;
  featureGroup.disabledClick();
  if (updateLookCartItem && IS_LOOK_JOURNEY) {
    updateLookCartItem();
  }
  const layout = getCurrentLayout();
  layout.updateLocalStorage();
}

/**
 * enable Feature common function
 * @param feature {object}
 * @param addToCart {function}
 * @param qtActiveCategory {string}
 */
export const enableRelatedFeature = (feature, addToCart, qtActiveCategory) => {
  const featureIsLocked = getFeatureIsLocked(feature);
  if (featureIsLocked || feature.notAvailableInVisualizer) {
    return false
  }
  let relatedFeaturesToEnable = [];
  const layout = getCurrentLayout();
  switch (feature.partId) {
    case "vanity_sink":
    case "vanity_faucet": {
      const relatedPartId = feature.partId === "vanity_sink" ? "vanity_faucet" : "vanity_sink";
      const relatedFeatureGroup = layout.getFeatureGroupByPartId(relatedPartId);
      if (relatedFeatureGroup) {
        relatedFeatureGroup.disabled = false;
        Object.keys(relatedFeatureGroup.layerPositions).forEach(position => {
          if ((position !== "C" && feature.metaData.layerPosition === "C") || (feature.metaData.layerPosition !== "C" && position === "C")) {
            relatedFeatureGroup.layerPositions[position] = null; // erase sibling positions
            return
          }
          const someFeature = relatedFeatureGroup.getMatchingPositionFeature(position);
          if (someFeature) {
            relatedFeatureGroup.layerPositions[position] = someFeature;
          }
        })
      // } else if (relatedFeatureGroup && !relatedFeatureGroup.layerPositions[feature.metaData.layerPosition]) {
      //   const someFeature = relatedFeatureGroup.getMatchingPositionFeature(feature.metaData.layerPosition);
      //   if (someFeature) {
      //     relatedFeaturesToEnable.push(someFeature);
      //     relatedFeatureGroup.disabled = false;
      //     relatedFeatureGroup.layerPositions[feature.metaData.layerPosition] = someFeature;
      //   }
      }
      break;
    }
    case "toilet": {
      ["toilet_brush", "paper_holder"].forEach(partId => {
        const relatedFeatureGroup = layout.getFeatureGroupByPartId(partId);
        if (relatedFeatureGroup && relatedFeatureGroup.disabled && relatedFeatureGroup.activeFeature) {
          relatedFeatureGroup.disabled = false;
          relatedFeaturesToEnable.push(relatedFeatureGroup.activeFeature);
        }
      })
      break;
    }
    case "bathtub_body":
    case "bathtub_fixture": {
      const relatedPartId = feature.partId === "bathtub_body" ? "bathtub_fixture" : "bathtub_body";
      const relatedFeatureGroup = layout.getFeatureGroupByPartId(relatedPartId);
      if (relatedFeatureGroup && relatedFeatureGroup.disabled && relatedFeatureGroup.activeFeature) {
        relatedFeatureGroup.disabled = false;
        relatedFeaturesToEnable.push(relatedFeatureGroup.activeFeature);
      }
      break;
    }
    default:
      break;
  }
  if (relatedFeaturesToEnable.length && addToCart) {
    relatedFeaturesToEnable.forEach(relatedFeature => {
      if (checkIfFeatureCanBeAddedToTheCart(relatedFeature)) {
        addToCart([getCartItemObject(relatedFeature, 1, qtActiveCategory)]);
      }
    })
  }
}

/**
 * returns lixil stock available quantity
 * @param feature {featureInterface}
 * @returns {boolean}
 */
export const isInStock = (feature) => {
  if (document.kvConfig.visualizerType !== "bvLook") {
    return true
  }
  const {settings} = store.getState().fabricatorState;
  if (settings.enableASInventorySync && feature.dbFeature.isFromAsRoom
    || (settings.enableGInventorySync && !feature.dbFeature.isFromAsRoom)) {
    const bundledFeatures = getBundledFeatures(feature);
    if (bundledFeatures.length > 1) { // fix for ROUGH VALVE BODIES assigned on Shower Fixtures
      return bundledFeatures.some(relFeature => relFeature.dbFeature.lixilAvailableQty > 0)
    }
    return feature.dbFeature.lixilAvailableQty > 0
  }
  if (settings.useTrumbleApi && feature.quantityOptions && feature.quantityOptions.trumbleQty === "0") {
    return false
  }
  return true
}
/**
 * returns lixil stock available quantity for cart item
 * @param cartItem {cartItemInterface}
 * @returns {boolean}
 */
export const cartItemInStock = (cartItem) => {
  // return cartItem.relatedProductParent ? isInStock(cartItem.relatedProductParent) : isInStock(cartItem.cartFeature)
  return isInStock(cartItem.cartFeature)
}

/**
 * returns lixil sku first part 0328400.222 -> 0328400
 * @param sku {string}
 * @returns {string}
 */
export const getSkuWithoutDot = (sku) => {
  if (!sku) {
    return ""
  }
  return sku.split(".")[0]
}

/**
 * returns lixil stock available quantity for cart item
 * @param sku {string}
 * @returns {string}
 */
export const getProductIdFromSku = (sku) => {
  if (sku.includes(".")) {
    return getSkuWithoutDot(sku) // "7075100.002" => "7075100"
  }
  if (sku.includes("-")) {
    return sku.split("-")[0] // "A8001L-RHO.020" => "A8001L-RHO"
  }
  return sku.substr(0, 5) + "_" + sku.substr(sku.length - 1); // "26635GN0" => "26635_0"
}

/**
 * returns a list needed for integration
 * @param cartItems {array}
 * @param forCart {boolean}
 * @returns {array}
 */
export const getPostCartItemsList = (cartItems, forCart = false) => {
  return cartItems
    .filter(cartItem => {
      let match = !(cartItem.cartFeature.dbFeature.sharedFeatureName || "").includes("undle sku")
        && (!featureIsBundle(cartItem.cartFeature, cartItems) || (forCart && cartItemInStock(cartItem)));
      if (match && forCart) {
        match = cartItem.quantity > 0 && cartItemInStock(cartItem) && !cartItem.isRemoved;
      }
      return match
    })
    .map(cartItem => {
      const sku = cartItem.cartFeature.dbFeature.sku || "";
      const lixilProductId = cartItem.cartFeature.dbFeature.lixilProductId || getProductIdFromSku(sku);
      return {
        "VariantId": sku, // "26635GN0"
        "ProductId": lixilProductId, // "26635_0"
        "Quantity": "" + cartItem.quantity // "1"
      }
    })
}

/**
 * @param feature {featureInterface}
 * @param cartItems {array}
 * @returns {boolean}
 */
export const featureIsBundle = (feature, cartItems) => {
  if (!feature) {
    return false
  }
  const relatedFeatures = getBundledFeatures(feature);
  return Array.isArray(relatedFeatures) && !!relatedFeatures.length && cartItems.some(cItem => {
    return cItem.relatedProductParent && cItem.relatedProductParent.dbFeature.id === feature.dbFeature.id;
  })
}

/**
 * returns bundled features
 * @param feature {featureInterface}
 * @returns {array}
 */
export const getBundledFeatures = (feature) => {
  if (Array.isArray(feature.dbFeature.bundledFeatures) && feature.dbFeature.bundledFeatures.length) {
    // console.log("relatedFeatures", relatedFeatures);
    let {relatedFeaturesFound} = getRelatedFeaturesFromLayout(feature.dbFeature.bundledFeatures, feature);
    // console.log("relatedFeaturesFound", relatedFeaturesFound);
    return relatedFeaturesFound
  }
  return []
}

/**
 * returns bundled features
 * @param feature {featureInterface}
 * @returns {array}
 */
export const getAvailableFeatureContent = (feature) => {
  if (!feature) {
    return []
  }
  const availableFeatureContent = []; // ["description", "features", "reviews", "faq"]
  if (feature.dbFeature.description) {
    availableFeatureContent.push("description");
  }
  if (feature.dbFeature.subDescription) {
    availableFeatureContent.push("features");
  }
  return availableFeatureContent
}

/**
 * returns bundled features
 * @param activePreset {number}
 * @param bvPresets {array}
 * @returns lookId {number}
 */
export const getLookId = (activePreset, bvPresets) => {
  const room = bvPresets.find(preset => preset.id === activePreset);
  return room ? room.lookId : 0
}

/**
 * returns URL start for look and product journeys
 * @param withSlash {boolean}
 * @returns string {string}
 */
export const getLookIdUrl = (withSlash = true) => {
  const {activePreset, bvPresets} = store.getState().bathroomVisualizerState;
  const lookId = getLookId(activePreset, bvPresets);
  return withSlash ? `/look-${lookId}/` : `/look-${lookId}`
}

/**
 * returns feature url name
 * @param feature {featureInterface}
 * @param withCategory {boolean}
 * @returns string {string}
 */
export const getFeatureUrl = (feature, withCategory = false) => {
  if (!feature || !feature.dbFeature) {
    return ""
  }

  let finalUrl = getLookIdUrl();
  const urlName = feature.dbFeature.sku || feature.dbFeature.sharedFeatureName; // check sku first! old support

  if (withCategory) {
    const urlPartId = feature.partId.replace(/_/gi, "-");
    return finalUrl + `${urlPartId}/${urlName}`
  } else {
    return finalUrl + urlName
  }
}

/**
 * returns category url
 * @param partId {string}
 * @returns string {string}
 */
export const getCategoryOnlyUrl = (partId = "") => {
  let finalUrl = getLookIdUrl();
  return finalUrl + partId.replace(/_/gi, "-")
}

/**
 * returns bundled features
 * @param updateCanvasCount {function}
 */
export const revertFeatureBeforeChange = (updateCanvasCount) => {
  if (IS_PRODUCT_JOURNEY) {
    return
  }
  const featureGroups = getLookList();
  if (featureGroups) {
    featureGroups.forEach(parentObject => {
      if (parentObject.featureGroup && parentObject.featureGroup.featureBeforeChange
        && checkIfFeatureCanBeAddedToTheCart(parentObject.featureGroup.featureBeforeChange)) {
        if (parentObject.featureGroup.layerPositions) {
          Object.keys(parentObject.featureGroup.layerPositions).forEach(position => {
            if (parentObject.featureGroup.layerPositions[position]) {
              const sameFeature = parentObject.featureGroup.getMatchingPositionFeature(position, parentObject.featureGroup.featureBeforeChange);
              if (sameFeature) {
                parentObject.featureGroup.layerPositions[position] = sameFeature;
              }
            }
          })
        }
        if (!parentObject.featureGroup.disabled) {
          parentObject.featureGroup.activeFeature = parentObject.featureGroup.featureBeforeChange;
        }
        parentObject.featureGroup.featureBeforeChange = null;
        updateCanvasCount();
      }
    })
  }
}

/**
 * returns filtered features by metadata field
 * @param navFeatures {object}
 * @param matchFeature {object}
 * @param metadataField {string}
 */
export const filterFeaturesByMetadata = (navFeatures, matchFeature, metadataField) => {
  return navFeatures.filter((navFeature) => {
    return navFeature.part.metaData[metadataField] && navFeature.part.metaData[metadataField].some((mType) => {
      return matchFeature.metaData[metadataField] &&
        matchFeature.metaData[metadataField].includes(mType)
    })
  })
}

/**
 * returns default camera preset
 * @param bvPresets {array}
 * @param lookId {string|number}
 * @returns lookId {number}
 */
export const getDefaultPresetByLook = (bvPresets, lookId) => {
  const numLookId = +lookId;
  const allMatchedPresets = bvPresets.filter((preset) => preset.lookId === numLookId);
  if (!allMatchedPresets.length) {
    return null
  }
  switch (numLookId) {
    case 5:
      return allMatchedPresets.find(preset => preset.scene === "1" && (preset.id === 22)) || allMatchedPresets[0];
    default:
      return allMatchedPresets.find(preset => preset.scene === "1")
  }
}

/**
 * open share link
 * @param buttonKey {string}
 * @param afterSaveUrl {string}
 * @param amazonImageUrl {string}
 */
const createShareUrl = (buttonKey, afterSaveUrl, amazonImageUrl) => {
  const shareUrl = afterSaveUrl; // || propsShareUrl;
  const shareImageUrl = amazonImageUrl || shareImage[document.kvConfig.visualizerType];
  switch (buttonKey) {
    case SHARE_BUTTONS.facebook: {
      const metaTag = document.querySelector('meta[property="og:image"]');
      if (metaTag) {
        metaTag.setAttribute("content", shareImageUrl);
      }
      var domain = takeRight(window.location.hostname.split('.'), 2);
      if(domain[0] == "americanstandard-us") {
        domain[0] = "americanstandard." + domain[0];
      } else if(domain[0] == "grohe") {
        domain[0] = "grohe." + domain[0];
      }
      const fbUrl = "https://facebook.com/sharer/sharer.php?u=";
      const qfUrl = window.location.protocol + "//" + domain.join('.') + "/url?u=";
      const splittedHost = window.location.host.split(".") // ['grohe', 'qkandb', 'net']
      const topLevelDomain = "." + splittedHost[splittedHost.length - 1]; // ".com" or ".net"

      const shareUrlSplitted = shareUrl.split(topLevelDomain)
      return fbUrl + qfUrl + shareUrlSplitted[0] + topLevelDomain + encodeURIComponent(shareUrlSplitted[1])
    }
    case SHARE_BUTTONS.pinterest: {
      const imageUrl = encodeURIComponent(shareImageUrl);
      return `//pinterest.com/pin/create/button/?url=${encodeURIComponent(shareUrl)}&media=${imageUrl}`
    }
    case SHARE_BUTTONS.twitter: {
      return `//twitter.com/share?url=${encodeURIComponent(shareUrl)}&text=Click Here to Design your Dream Bathroom!`
    }
    default:
      return ""
  }
}

/**
 * open share link
 * @param buttonKey {string}
 */
const getShareWindowReference = (buttonKey) => {
  switch (buttonKey) {
    case SHARE_BUTTONS.facebook: {
      // target: facebook-share-dialog
      return window.open("", '_blank', getShareConfig({width: 630, height: 420}))
    }
    case SHARE_BUTTONS.pinterest: {
      return window.open("", '_blank', getShareConfig({width: 880, height: 554}))
    }
    case SHARE_BUTTONS.twitter: {
      return window.open("", '_blank', getShareConfig({width: 550, height: 470}))
    }
    default:
      return window.open()
  }
}

/**
 * save Quote and open share link
 * @param buttonKey {string}
 * @param propsShareUrl {string}
 * @param saveNeeded {boolean}
 * @param imageUrl {string}
 */
export const saveQuoteAndShare = async (buttonKey, propsShareUrl, saveNeeded = true, imageUrl = "") => {
  if (!propsShareUrl) {
    propsShareUrl = store.getState().serverState.shareUrl;
  }
  let windowReference = null;
  const isSafari = browserIsSafari();
  if (isSafari) {
    windowReference = getShareWindowReference(buttonKey);
  }
  let responseData = null;
  if (saveNeeded) {
    loaderEnable();
    responseData = await saveQuote({
      submit: true,
      showReceipt: false
    });
    if (!responseData) {
      return false
    }
    document.kvConfig.jobId = responseData.jobId;
    if (responseData.imageUrl) {
      imageUrl = responseData.imageUrl;
      store.dispatch(setAmazonImageUrl(responseData.imageUrl));
    }
    loaderDisable();
  }

  if (!isSafari) {
    windowReference = getShareWindowReference(buttonKey);
  }

  windowReference.location = createShareUrl(buttonKey, getShareUrl(document.kvConfig.jobId) || propsShareUrl, imageUrl);

}

/**
 * returns cart items array
 * @param neededFeature {featureInterface}
 * @param quantity {number}
 * @param qtActiveCategory {string}
 * @param addedBy {string}
 * @returns {array}
 */
export const getCartItemsArrayFromFeature = (neededFeature, quantity, qtActiveCategory, addedBy = CART_ITEM_ADDED_BY.user) => {
  const firstCartItem = getCartItemObject(neededFeature, quantity, qtActiveCategory);
  firstCartItem.addedBy = addedBy;
  let itemsToAdd = [firstCartItem];
  const relatedFeatures = getBundledFeatures(neededFeature);
  if (Array.isArray(relatedFeatures) && relatedFeatures.length) {
    relatedFeatures.forEach((relatedFeature) => {
      const cartItem = getCartItemObject(relatedFeature, quantity, qtActiveCategory);
      cartItem.relatedProductParent = neededFeature;
      cartItem.addedBy = addedBy;
      itemsToAdd.push(cartItem);
    })
  }
  return itemsToAdd
}


/**
 * Checks available meta options for feature
 * @param lookActiveFeature {featureInterface}
 * @param filterGroups {array}
 * @param metaName {string}
 * @returns  {array} array of objects
 */
export const getSameFeatureWithAnotherMeta = (lookActiveFeature, filterGroups, metaName = "MatchingFeature") => {
  if (!lookActiveFeature) {
    return null
  }

  const parent = lookActiveFeature.getParent();
  const activeFeatureValue = Array.isArray(lookActiveFeature.metaData[metaName]) ? lookActiveFeature.metaData[metaName][0] : null;

  let optionFeatures = activeFeatureValue ? [{
    feature: lookActiveFeature,
    metaValue: activeFeatureValue
  }] : [];

  const layout = getCurrentLayout();
  const navItems = layout.designNavigationItems({part: parent}, {}, "", lookActiveFeature, true);
  const activeFeatureCollectionName = lookActiveFeature.getCollectionName(filterGroups);
  const customFullDisplayName = lookActiveFeature.getCustomFullDisplayName();

  for (let i = 0; i < navItems.length; i++) {
    const checkFeature = navItems[i].part;
    if ((checkFeature.dbFeature.sharedFeatureName === "MatchingFeature") && checkFeature.getCustomFullDisplayName() !== customFullDisplayName) {
      continue
    }
    if ((checkFeature.dbFeature.sharedFeatureName !== "MatchingFeature") && getSkusDifferences(checkFeature.dbFeature.sharedFeatureName, lookActiveFeature.dbFeature.sharedFeatureName) > 1) {
      continue
    }
    // // console.log("checkFeature", checkFeature);
    const metaValue = Array.isArray(checkFeature.metaData[metaName]) ? checkFeature.metaData[metaName][0] : null;
    if (!metaValue) {
      continue
    }
    if (allowCheckForCollection(checkFeature) && (checkFeature.getCollectionName(filterGroups) !== activeFeatureCollectionName)) {
      continue
    }
    if ((metaName === "MatchingFeature") && lookActiveFeature.metaData["MatchingFeature"] && !isEqual(lookActiveFeature.metaData["MatchingFeature"], checkFeature.metaData["MatchingFeature"])) {
      continue
    }
    if (optionFeatures.every(optFeature => optFeature.metaValue !== metaValue)) {
      optionFeatures.push({
        feature: checkFeature,
        metaValue
      })
    }
  }

  return optionFeatures

};

export const getFeatureSectionIsHidden = (lookActiveFeature) => {
  return !lookActiveFeature || (LOOK_WALLS.includes(lookActiveFeature.partId) && !(
    lookActiveFeature.partId === "front_wall" && (document.kvConfig.visualizerType === "Fireplace")
  ))
  // const layout = getCurrentLayout();
  // return lookActiveFeature && (
  //   (!layout.partIdExistInQtCategory(lookActiveFeature.partId, true) || LOOK_WALLS.includes(lookActiveFeature.partId)));
}

/**
 * returns parent feature group
 * @param lookFeature {featureInterface}
 * @param lookActiveCategory {string}
 * @returns {object} featureGroup
 */
export const getFeatureParent = (lookFeature, lookActiveCategory = "") => {
  if (lookFeature && lookFeature.getParent) {
    return lookFeature.getParent()
  }
  const layout = getCurrentLayout();
  return layout.getFeatureGroupByPartId(lookActiveCategory);
}

/**
 * returns feature property name
 * @param lookActiveFeature {featureInterface}
 * @returns {string}
 */
export const getPropertyToMatchActiveFeature = (lookActiveFeature) => {
  if (lookActiveFeature.dbFeature.sharedFeatureName) {
    // keep old logic, some features are missing the sku, so we have to have the code below
    return "sharedFeatureName"
  }
  // original check for other tools
  return "sku"
}

/**
 * Checks if list feature is currently active
 * @param lookFeature {featureInterface}
 * @param lookActiveFeature {featureInterface}
 * @param lookActiveCategory {string}
 * @param featureGroup {object}
 * @returns {boolean}
 */
export const getFeatureIsActive = (lookFeature, lookActiveFeature, lookActiveCategory = "", featureGroup) => {
  if (!lookFeature) {
    return false
  }
  let checkFeature = lookActiveFeature;
  if (!checkFeature && lookActiveCategory) {
    const parentFeatureGroup = featureGroup || getFeatureParent(lookFeature, lookActiveCategory);
    if (parentFeatureGroup) {
      checkFeature = parentFeatureGroup.activeNotInVisualizerFeature || parentFeatureGroup.activeFeature;
    }
  }
  return checkFeature && ((lookFeature.dbFeature.id === checkFeature.dbFeature.id)
    || (lookFeature.getSplittedFeatureName() === checkFeature.getSplittedFeatureName())
  )
}

/**
 * Checks if list feature is currently active
 * @param parentObject {object}
 * @param layoutType {number|string}
 * @param scene {number|string}
 * @param [position] {string}
 * @returns {boolean}
 */
export const removeLookPointFor = (parentObject, layoutType, scene, position) => {
  const layout = getCurrentLayout();
  if ((layout.type === layoutType) && (layout.scene === scene)) {
    if (position) {
      return parentObject.layerPosition !== position
    }
    return false
  }
  return true
}

/**
 * Checks if two strings are almost the same (to compare SKU\s, for example 0328400.222 and 0328800.222)
 * @param sku1 {string}
 * @param sku2 {string}
 * @returns {number}
 */
export const getSkusDifferences = (sku1, sku2) => {
  if (sku1 === sku2) {
    return 0
  }
  let differences = 0;
  sku1.split("").forEach((letter, letterIndex) => {
    if (!sku2[letterIndex] || letter !== sku2[letterIndex]) {
      differences++;
    }
  })
  return differences
}

/**
 * filters removed cartItems
 * @param cartItems {[cartItemInterface]}
 * @param withoutNotInStock {boolean}
 * @param withoutZeroQuantity {boolean}
 * @param parentsOnly {boolean}
 * @returns {[cartItemInterface]}
 */
export const filterCartItems = (cartItems, withoutNotInStock = false, withoutZeroQuantity = false, parentsOnly = false) => {
  return cartItems.filter(cItem => {
    if (withoutZeroQuantity && cItem.quantity < 1) {
      return false
    }
    if (withoutNotInStock) {
      // if (cItem.relatedProductParent && !isInStock(cItem.relatedProductParent)) {
      //   return false
      // }
      if (!isInStock(cItem.cartFeature) || getBundledFeatures(cItem.cartFeature).length) {
        return false
      }
    }
    if (IS_PRODUCT_JOURNEY) {
      return cItem.addedBy === CART_ITEM_ADDED_BY.user && !cItem.isRemoved
    }
    if (parentsOnly) {
      return !cItem.relatedProductParent
    }
    return !cItem.isRemoved
  })
}

/**
 * sorts CartItems for List Page
 * @param cartItems {[cartItemInterface]}
 * @returns {[cartItemInterface]}
 */
export const sortCartItemsForListPage = (cartItems) => {
  return cartItems
    .filter(cItem => !cItem.relatedProductParent)
    .sort((a, b) => featureIsBundle(a.cartFeature, cartItems) - featureIsBundle(b.cartFeature, cartItems))
    .sort((a, b) => (b.addedBy === CART_ITEM_ADDED_BY.user) - (a.addedBy === CART_ITEM_ADDED_BY.user) || cartItemInStock(b) - cartItemInStock(a))
}

/**
 * returns array of deliveries
 * @param lookOpened {boolean}
 * @param lookActiveCategory {string}
 * @param lookActiveFeature {featureInterface}
 * @param lookCompatabilityAlert {object}
 * @returns {boolean}
 */
export const hideFeatureSection = (lookOpened, lookActiveCategory, lookActiveFeature, lookCompatabilityAlert) => {
  const layout = getCurrentLayout();
  let showFeaturesOnly = !lookCompatabilityAlert.visible && lookOpened && lookActiveCategory && !layout.partIdExistInQtCategory(lookActiveCategory, true);
  if (!showFeaturesOnly && lookActiveFeature && !lookCompatabilityAlert.visible) {
    showFeaturesOnly = !isFeatureEnabled(lookActiveFeature, "quotingTool");
  }
  return showFeaturesOnly
}

/**
 * returns array of deliveries
 * @param partId {string}
 * @param layout {object}
 * @param fabricatorState {object}
 * @returns {boolean}
 */
export const hardCodelyRemovedClicking = (partId, layout = getCurrentLayout(), fabricatorState = null) => {
  if (document.kvConfig.visualizerType !== "bvLook") {
    return false
  }
  if (getCurrentRoomType(layout) === LOOK_ROOM_TYPES.as) {
    if (fabricatorState && fabricatorState.isCoburn && partId === "shower_door" && layout.parts.Shower) {
      const showerDoorFeatureGroup = layout.parts.Shower.showerDoor;
      if (showerDoorFeatureGroup && showerDoorFeatureGroup.activeFeature
        && (showerDoorFeatureGroup.activeFeature.dbFeature.sharedFeatureName === "Hinge Door.002"
          || showerDoorFeatureGroup.activeFeature.dbFeature.sharedFeatureName === "Barn Door.002")) {
        return true
      }
    }
    return false
  }
  return partId === "bathtub_body" || partId === "vanity_cabinet"
}

export const getWizardWithFirstStepCurrentPartId = (wizardList, partId) => {
  return wizardList.find(wizardItem => {
    if (!wizardItem.openWizardOnClick) {
      return false
    }
    const firstStepGroups = getCurrentGroups(wizardItem, 0, {});
    if (firstStepGroups && firstStepGroups.length === 1 && firstStepGroups[0].nodeType === "group") {
      const featureGroup = getFeatureGroupFromWizardGroup(firstStepGroups[0]);
      if (featureGroup && featureGroup.partId === partId) {
        return true
      }
    }
    return false
  });
}
